import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sun, Star, Moon, Sparkles, Music, UtensilsCrossed, BellRing } from "lucide-react";
import api from "@/lib/api";

const ICONS = { Sun, Star, Moon, Sparkles, Music, UtensilsCrossed, BellRing };

const EASE = [0.16, 1, 0.3, 1];

const EVENT_PLACE = {
  "@type": "Place",
  name: "Neem Karoli Baba Mandir & Ashram — Osho Kaivalya Dham",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1642 FM 1310",
    addressLocality: "Denison",
    addressRegion: "TX",
    postalCode: "75020",
    addressCountry: "US",
  },
};

const EVENT_ORGANIZER = {
  "@type": "Organization",
  "@id": "https://neemkarolibabadallas.org/#org",
  name: "Neem Karoli Baba Mandir & Ashram — Osho Kaivalya Dham",
  url: "https://neemkarolibabadallas.org/",
};

function nextAnnual(month, day) {
  const now = new Date();
  let d = new Date(now.getFullYear(), month - 1, day);
  if (d < now) d = new Date(now.getFullYear() + 1, month - 1, day);
  return d.toISOString().slice(0, 10);
}

function nextSaturday() {
  const d = new Date();
  d.setDate(d.getDate() + (((6 - d.getDay()) + 7) % 7 || 7));
  return d.toISOString().slice(0, 10);
}

function buildEventsJsonLd() {
  const base = {
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: EVENT_PLACE,
    organizer: EVENT_ORGANIZER,
    url: "https://neemkarolibabadallas.org/#festivals",
    image: "https://neemkarolibabadallas.org/images/hanuman-sunset-2.webp",
  };
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Event",
        ...base,
        name: "Mahasamadhi Bhandara — Neem Karoli Baba",
        startDate: nextAnnual(9, 11),
        description: "The anniversary of Maharajji's mahasamadhi (1973): a full day of prayer, Hanuman Chalisa, and the great bhandara at the Neem Karoli Baba temple near Dallas–Fort Worth.",
      },
      {
        "@type": "Event",
        ...base,
        name: "Mandir Foundation Day",
        startDate: nextAnnual(12, 4),
        description: "Anniversary of the mandir's inauguration (Dec 4, 2023) — Baba's temple's Texas birthday, celebrated with family, aarti and bhandara.",
      },
      {
        "@type": "Event",
        ...base,
        name: "Weekly Saturday Satsang & Bhandara",
        startDate: `${nextSaturday()}T17:00:00-05:00`,
        description: "Every Saturday: darshan, aarti, Hanuman Chalisa, satsang and a sanctified meal (bhandara) at DFW's Neem Karoli Baba temple in Denison, TX.",
      },
    ],
  };
}

const FEATURED = [
  {
    date: "September 11",
    title: "Mahasamadhi Bhandara",
    text: "The anniversary of Baba's mahasamadhi (1973) — the holiest day of our year. A full day of prayer, Hanuman Chalisa, and the great bhandara, because feeding everyone is how we remember Him.",
    testid: "festival-mahasamadhi",
  },
  {
    date: "December 4",
    title: "Mandir Foundation Day",
    text: "The anniversary of our own mandir's inauguration in 2023 — Baba's temple's Texas birthday, celebrated with family.",
    testid: "festival-foundation-day",
  },
];

const FESTIVALS = [
  {
    icon: Sun,
    season: "Chaitra Purnima · March–April",
    title: "Hanuman Jayanti",
    text: "The birthday of Hanuman-ji — abhishek at the murti, 108 recitations of the Chalisa, and a grand bhandara.",
    testid: "festival-hanuman-jayanti",
  },
  {
    icon: Star,
    season: "Chaitra Navami · March–April",
    title: "Ram Navami",
    text: "The appearance day of Shri Ram — Akhand Ramayana path and special aarti.",
    testid: "festival-ram-navami",
  },
  {
    icon: Moon,
    season: "July · Full Moon",
    title: "Guru Purnima",
    text: "The full moon of the Guru — an evening of gratitude at Baba's altar, with bhajans and paduka puja.",
    testid: "festival-guru-purnima",
  },
  {
    icon: Sparkles,
    season: "October–November",
    title: "Diwali at the Mandir",
    text: "A thousand diyas around Hanuman-ji — Lakshmi puja, fireworks of devotion, and sweets for all.",
    testid: "festival-diwali",
  },
  {
    icon: Music,
    season: "Every Saturday",
    title: "Weekly Satsang & Bhandara",
    text: "The heartbeat of the mandir — darshan, aarti, Chalisa, and a sanctified meal, every single week.",
    testid: "festival-weekly-satsang",
  },
];

export default function FestivalCalendar() {
  const [featured, setFeatured] = useState(FEATURED);
  const [festivals, setFestivals] = useState(FESTIVALS);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "festival-events-jsonld";
    script.textContent = JSON.stringify(buildEventsJsonLd());
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  useEffect(() => {
    api.get("/events").then((r) => {
      const evts = r.data.events || [];
      const feat = evts.filter((e) => e.kind === "featured")
        .map((e) => ({ date: e.date, title: e.title, text: e.text, testid: `festival-${e.slug}` }));
      const list = evts.filter((e) => e.kind === "festival")
        .map((e) => ({ icon: ICONS[e.icon] || Sun, season: e.season, title: e.title, text: e.text, testid: `festival-${e.slug}` }));
      if (feat.length) setFeatured(feat);
      setFestivals(list);
    }).catch(() => {});
  }, []);

  return (
    <section id="festivals" data-testid="festivals-section" className="py-20 sm:py-28 bg-[#FDFBF7] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow mb-4">07 · The Year at the Mandir</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-950" data-testid="festivals-heading">
            Festivals &amp; Sacred Days
          </h2>
          <p className="mt-5 text-stone-600 leading-relaxed">
            In Baba's tradition, every holy day ends the same way — with a bhandara, where everyone eats
            together as family. Mark these days; the kitchen will be warm.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {featured.map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
              data-testid={f.testid}
              className="group relative overflow-hidden rounded-3xl bg-[#1C1917] p-8 sm:p-10 hover:-translate-y-1.5 transition-transform duration-500"
            >
              <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{ backgroundImage: "radial-gradient(circle at 80% 10%, #F59E0B 0%, transparent 50%)" }} />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <p className="font-display text-3xl sm:text-4xl font-semibold text-amber-400">{f.date}</p>
                  <UtensilsCrossed className="h-7 w-7 text-amber-500/70 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl text-amber-50 mt-4">{f.title}</h3>
                <p className="mt-3.5 text-sm leading-relaxed text-stone-400">{f.text}</p>
                <p className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-amber-300/90">
                  <BellRing className="h-3.5 w-3.5" /> Grand Bhandara — All Are Fed
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {festivals.map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: EASE }}
              data-testid={f.testid}
              className="group bg-white rounded-2xl border border-amber-900/10 p-6 hover:border-amber-600/40 hover:shadow-lg hover:shadow-amber-900/5 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-b from-amber-100 to-orange-100 border border-amber-900/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <f.icon className="h-5 w-5 text-orange-700" />
                </span>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-amber-700">{f.season}</p>
              </div>
              <h3 className="font-display text-lg text-amber-950">{f.title}</h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="mt-10 text-center text-sm text-stone-500"
          data-testid="festivals-note"
        >
          Lunar festival dates shift each year — exact dates are announced on our satsang list.
          <a href="#footer" data-testid="festivals-newsletter-link" className="ml-1 font-semibold text-amber-800 hover:text-orange-700 transition-colors">
            Join it below →
          </a>
        </motion.p>
      </div>
    </section>
  );
}
